import { useState, useRef, useCallback, useEffect } from 'react';
import { AlphaTabApi } from '@coderline/alphatab';

export interface UseSyncedVideoPlaybackOptions {
  alphaTabApi: AlphaTabApi | null;
  videoElement: HTMLVideoElement | null;
  initialOffset?: number;
  /** BPM ratio: mediaBpm / scoreTempo. Used to keep video and AlphaTab synced. */
  baseSpeed?: number;
  /** Original tempo from score in BPM */
  scoreTempo: number;
  /** Current user-selected tempo in BPM */
  currentTempo: number;
}

export interface UseSyncedVideoPlaybackReturn {
  isReady: boolean;
  isPlaying: boolean;
  offset: number;
  nudgeOffset: (deltaMs: number) => void;
  resetOffset: () => void;
  setOffset: (ms: number) => void;
  play: () => void;
  pause: () => void;
  stop: () => void;
  seekToTime: (timeMs: number) => void;
  videoDuration: number;
  videoPosition: number;
}

/**
 * Syncs video playback with AlphaTab cursor.
 *
 * Offset semantics (matching audio sync behavior):
 * - Positive offset = cursor starts AFTER video (delay cursor start)
 *   Example: offset=2500 means video plays for 2.5s before tabs start
 * - Negative offset = cursor starts BEFORE video (delay video start)
 *   Example: offset=-1000 means tabs play for 1s before video starts
 */
export function useSyncedVideoPlayback({
  alphaTabApi,
  videoElement,
  initialOffset = 0,
  baseSpeed = 1.0,
  scoreTempo,
  currentTempo,
}: UseSyncedVideoPlaybackOptions): UseSyncedVideoPlaybackReturn {
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [offset, setOffsetState] = useState(initialOffset);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoPosition, setVideoPosition] = useState(0);

  // Refs
  const offsetRef = useRef(offset);
  const delayedStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we're at the beginning (for offset logic) vs. resuming from pause
  const isAtStartRef = useRef(true);

  // Calculate tempo ratio from BPM values
  const tempoRatio = scoreTempo > 0 ? currentTempo / scoreTempo : 1.0;
  const tempoRatioRef = useRef(tempoRatio);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    tempoRatioRef.current = tempoRatio;
  }, [tempoRatio]);

  // Update offset state when initialOffset prop changes
  // (useState only uses initial value on first render)
  // Also reset isAtStartRef so the new offset is applied on next play
  useEffect(() => {
    setOffsetState(initialOffset);
    isAtStartRef.current = true;
  }, [initialOffset]);

  // Computed ready state
  const isReady = Boolean(alphaTabApi && videoElement && videoDuration > 0);

  // Clear delayed start timeout
  const clearDelayedStart = useCallback(() => {
    if (delayedStartTimeoutRef.current) {
      clearTimeout(delayedStartTimeoutRef.current);
      delayedStartTimeoutRef.current = null;
    }
  }, []);

  // Setup video event listeners
  useEffect(() => {
    if (!videoElement) return;

    // Reset playback state when video element changes (e.g., layer switch)
    setIsPlaying(false);
    setVideoPosition(0);
    isAtStartRef.current = true;

    const handleLoadedMetadata = () => {
      setVideoDuration(videoElement.duration);
    };

    const handleTimeUpdate = () => {
      setVideoPosition(videoElement.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (alphaTabApi) {
        alphaTabApi.pause();
      }
    };

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);

    // If already loaded, get duration
    if (videoElement.duration) {
      setVideoDuration(videoElement.duration);
    }

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [videoElement, alphaTabApi]);

  // Play - handle offset by delaying either video or cursor start (only at beginning)
  const play = useCallback(() => {
    if (!videoElement || !alphaTabApi) return;

    clearDelayedStart();

    // If we're resuming from pause (not at start), re-sync AlphaTab to video position
    // before resuming. AlphaTab's pause() snaps cursor back to the last beat boundary,
    // so we need to restore the correct position based on where the video actually is.
    if (!isAtStartRef.current) {
      const videoTimeMs = videoElement.currentTime * 1000;
      const alphaTabTimeMs = Math.max(0, videoTimeMs - offsetRef.current);
      alphaTabApi.timePosition = alphaTabTimeMs;
      videoElement.play();
      alphaTabApi.play();
      setIsPlaying(true);
      return;
    }

    // Starting from the beginning - apply offset logic
    // Scale offset by video playback rate: at slower tempo, we need more real time
    // for the video to reach the same content position
    const currentOffset = offsetRef.current;
    const currentTempoRatio = tempoRatioRef.current;
    const adjustedOffset = currentOffset / currentTempoRatio;

    isAtStartRef.current = false; // Mark that we've started

    if (adjustedOffset >= 0) {
      // Positive offset: video starts first, cursor starts after delay
      // Example: offset=2500 at 50% tempo means wait 5000ms before tabs start moving
      videoElement.play();

      if (adjustedOffset === 0) {
        alphaTabApi.play();
      } else {
        // Delay cursor start (scaled for tempo)
        delayedStartTimeoutRef.current = setTimeout(() => {
          if (alphaTabApi) {
            alphaTabApi.play();
          }
        }, adjustedOffset);
      }
    } else {
      // Negative offset: cursor starts first, video starts after delay
      // Example: offset=-1000 at 50% tempo means wait 2000ms before video starts
      alphaTabApi.play();

      delayedStartTimeoutRef.current = setTimeout(() => {
        if (videoElement) {
          videoElement.play();
        }
      }, Math.abs(adjustedOffset));
    }

    setIsPlaying(true);
  }, [videoElement, alphaTabApi, clearDelayedStart]);

  // Pause
  const pause = useCallback(() => {
    clearDelayedStart();

    if (videoElement) {
      videoElement.pause();
    }
    if (alphaTabApi) {
      alphaTabApi.pause();
    }
    setIsPlaying(false);
  }, [videoElement, alphaTabApi, clearDelayedStart]);

  // Stop
  const stop = useCallback(() => {
    clearDelayedStart();

    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
    if (alphaTabApi) {
      alphaTabApi.stop();
    }
    setIsPlaying(false);
    setVideoPosition(0);
    isAtStartRef.current = true; // Reset to beginning
  }, [videoElement, alphaTabApi, clearDelayedStart]);

  // Seek both video and alphatab to a specific time
  const seekToTime = useCallback((timeMs: number) => {
    if (!videoElement || !alphaTabApi) return;

    const currentOffset = offsetRef.current;

    // Calculate video time accounting for offset
    // If offset is positive (video starts first), video time = alphatab time + offset
    // If offset is negative (alphatab starts first), video time = alphatab time - |offset|
    const videoTimeMs = timeMs + currentOffset;
    const videoTimeSec = Math.max(0, videoTimeMs / 1000);

    // Seek video
    videoElement.currentTime = videoTimeSec;

    // Seek alphatab - timePosition is in milliseconds at normal speed
    // We need to convert to the base time (without tempo adjustment)
    alphaTabApi.timePosition = timeMs;

    // Mark that we're no longer at start
    isAtStartRef.current = false;
    setVideoPosition(videoTimeSec);

  }, [videoElement, alphaTabApi]);

  // Offset controls
  const nudgeOffset = useCallback((deltaMs: number) => {
    setOffsetState(prev => prev + deltaMs);
  }, []);

  const resetOffset = useCallback(() => {
    setOffsetState(0);
  }, []);

  const setOffset = useCallback((ms: number) => {
    setOffsetState(ms);
  }, []);

  // Apply tempo based on BPM ratio
  // Video plays at tempoRatio speed (native speed when currentTempo === scoreTempo)
  // AlphaTab adjusts by baseSpeed to account for any BPM difference between media and score
  useEffect(() => {
    if (!videoElement || !alphaTabApi) return;

    // Video rate: tempoRatio (1.0 at native speed when currentTempo === scoreTempo)
    videoElement.playbackRate = tempoRatio;

    // AlphaTab: tempoRatio * baseSpeed
    // baseSpeed = mediaBpm / scoreTempo
    // This ensures AlphaTab stays synced with the video accounting for BPM differences
    const alphaTabSpeed = tempoRatio * baseSpeed;
    alphaTabApi.playbackSpeed = alphaTabSpeed;

  }, [tempoRatio, baseSpeed, videoElement, alphaTabApi, currentTempo, scoreTempo]);

  // Listen for user clicking on beats in AlphaTab to seek
  useEffect(() => {
    if (!alphaTabApi) return;

    const handleBeatMouseDown = (beat: { playbackStart: number } | null) => {
      if (!beat) return;

      // beat.playbackStart is in ticks, we need to convert to time
      // AlphaTab's playerPositionChanged gives us currentTime in ms for the current tick
      // We can use the masterBars to find the time, or directly seek using tick position

      // Get the tick position and convert to time
      // playbackStart is absolute tick position in the score
      const tickPosition = beat.playbackStart;

      // Convert ticks to milliseconds using AlphaTab's internal conversion
      // We set tickPosition which triggers internal time calculation
      if (videoElement) {
        // Save current playing state
        const wasPlaying = isPlaying;

        // Get the current tempo from score to calculate time
        // AlphaTab uses 960 ticks per quarter note (standard MIDI resolution)
        const score = alphaTabApi.score;
        if (score && score.tempo > 0) {
          // Time (ms) = (ticks / ticksPerQuarter) * (60000 / tempo)
          // Where ticksPerQuarter = 960 (AlphaTab's MIDI resolution)
          const ticksPerQuarterNote = 960;
          const timeMs = (tickPosition / ticksPerQuarterNote) * (60000 / score.tempo);

          // Seek to that time
          seekToTime(timeMs);

          // If we were playing, restart playback
          if (wasPlaying) {
            // Small delay to let seek complete
            setTimeout(() => {
              videoElement?.play();
              alphaTabApi?.play();
            }, 50);
          }
        }
      }
    };

    // Subscribe to beat mouse down event
    alphaTabApi.beatMouseDown.on(handleBeatMouseDown);

    return () => {
      alphaTabApi.beatMouseDown.off(handleBeatMouseDown);
    };
  }, [alphaTabApi, videoElement, isPlaying, seekToTime]);

  // Cleanup
  useEffect(() => {
    return () => {
      clearDelayedStart();
    };
  }, [clearDelayedStart]);

  return {
    isReady,
    isPlaying,
    offset,
    nudgeOffset,
    resetOffset,
    setOffset,
    play,
    pause,
    stop,
    seekToTime,
    videoDuration,
    videoPosition,
  };
}
